import { Injectable, signal, computed } from '@angular/core';
import { McDashboardService } from 'src/app/api/mc-dashboard.service';
import { NotesService } from 'src/app/api/notes.service';
import { RecruitingService } from 'src/app/api/recruiting.service';
import { FinanceService } from 'src/app/api/finance.service';
import { RegistrationService } from 'src/app/api/registration.service';
import {
  UiBulletin,
  KnowBe4Data,
  KnowBe4Averages,
  UiGlMail
} from 'src/app/api/types/mc-dashboard';
import { RecruitingCallCounts, CallGoals } from 'src/app/api/types/recruiting';
import { PaymentProgressStats, PaymentStats } from 'src/app/api/types/finance';
import { RecruitingGoals, RecruitingProgress } from 'src/app/api/types/registration';
import { Observable, of, forkJoin, combineLatest, startWith } from 'rxjs';
import { catchError, timeout, tap, finalize } from 'rxjs/operators';
import { CustomerService } from 'src/app/api';

export interface DashboardState {
  bulletins: UiBulletin[] | null;
  knowBe4Data: KnowBe4Data[];
  knowBe4Averages: KnowBe4Averages;
  glMail: UiGlMail[];
  callCounts: RecruitingCallCounts | null;
  callGoals: CallGoals | null;
  paymentProgress: PaymentProgressStats | null;
  yearlyPayments: PaymentStats[];
  registrationProgress: RecruitingProgress | null;
  registrationGoals: RecruitingGoals | null;
  callGraphData: any[] | null;
  registrationGraphData: any[] | null;
  paymentGraphData: any[] | null;
  isLoading: boolean;
  lastUpdated: Date | null;
}

export interface ComponentLoadingState {
  pulse: boolean;
  missionVisionValues: boolean;
  glMail: boolean;
  verseOfTheDay: boolean;
  knowBe4Graphs: boolean;
  individualStaffKpi: boolean;
  registrationStats: boolean;
  callStats: boolean;
  paymentStats: boolean;
  tripHistory: boolean;
  googleAnalytics: boolean;
  renewalProgress: boolean;
  miscStats: boolean;
  myRegistrationStats: boolean;
  myCallStats: boolean;
  myPotentialTrips: boolean;
  myConsultingWrapper: boolean;
  myOverdueRegiFees: boolean;
  myOverduePayments: boolean;
  recruitingScheduledActivities: boolean;
  consultingScheduledActivities: boolean;
  partnerScheduledActivities: boolean;
  allScheduledActivities: boolean;
  supervisorDashboard: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class DashboardDataService {
  // Private signals for internal state management
  private _bulletins = signal<UiBulletin[] | null>(null);
  private _knowBe4Data = signal<KnowBe4Data[]>([]);
  private _knowBe4Averages = signal<KnowBe4Averages>({} as KnowBe4Averages);
  private _glMail = signal<UiGlMail[]>([]);
  private _callCounts = signal<RecruitingCallCounts | null>(null);
  private _callGoals = signal<CallGoals | null>(null);
  private _paymentProgress = signal<PaymentProgressStats | null>(null);
  private _yearlyPayments = signal<PaymentStats[]>([]);
  private _registrationProgress = signal<RecruitingProgress | null>(null);
  private _registrationGoals = signal<RecruitingGoals | null>(null);
  private _callGraphData = signal<any[] | null>(null);
  private _registrationGraphData = signal<any[] | null>(null);
  private _paymentGraphData = signal<any[] | null>(null);
  private _isLoading = signal<boolean>(false);
  private _lastUpdated = signal<Date | null>(null);

  // Public readonly signals for components to access
  readonly bulletins = this._bulletins.asReadonly();
  readonly knowBe4Data = this._knowBe4Data.asReadonly();
  readonly knowBe4Averages = this._knowBe4Averages.asReadonly();
  readonly glMail = this._glMail.asReadonly();
  readonly callCounts = this._callCounts.asReadonly();
  readonly callGoals = this._callGoals.asReadonly();
  readonly paymentProgress = this._paymentProgress.asReadonly();
  readonly yearlyPayments = this._yearlyPayments.asReadonly();
  readonly registrationProgress = this._registrationProgress.asReadonly();
  readonly registrationGoals = this._registrationGoals.asReadonly();
  readonly callGraphData = this._callGraphData.asReadonly();
  readonly registrationGraphData = this._registrationGraphData.asReadonly();
  readonly paymentGraphData = this._paymentGraphData.asReadonly();
  readonly isLoading = this._isLoading.asReadonly();
  readonly lastUpdated = this._lastUpdated.asReadonly();

  // Simplified component loading states
  private _componentLoadingState = signal<ComponentLoadingState>({
    pulse: true,
    missionVisionValues: false,
    glMail: true,
    verseOfTheDay: false,
    knowBe4Graphs: true,
    individualStaffKpi: false,
    registrationStats: true,
    callStats: true,
    paymentStats: true,
    tripHistory: false,
    googleAnalytics: false,
    renewalProgress: false,
    miscStats: false,
    myRegistrationStats: false,
    myCallStats: false,
    myPotentialTrips: false,
    myConsultingWrapper: false,
    myOverdueRegiFees: false,
    myOverduePayments: false,
    recruitingScheduledActivities: false,
    consultingScheduledActivities: false,
    partnerScheduledActivities: false,
    allScheduledActivities: false,
    supervisorDashboard: false
  });

  readonly componentLoadingState = this._componentLoadingState.asReadonly();

  // Computed state for components that need the full state
  readonly state = computed<DashboardState>(() => ({
    bulletins: this._bulletins(),
    knowBe4Data: this._knowBe4Data(),
    knowBe4Averages: this._knowBe4Averages(),
    glMail: this._glMail(),
    callCounts: this._callCounts(),
    callGoals: this._callGoals(),
    paymentProgress: this._paymentProgress(),
    yearlyPayments: this._yearlyPayments(),
    registrationProgress: this._registrationProgress(),
    registrationGoals: this._registrationGoals(),
    callGraphData: this._callGraphData(),
    registrationGraphData: this._registrationGraphData(),
    paymentGraphData: this._paymentGraphData(),
    isLoading: this._isLoading(),
    lastUpdated: this._lastUpdated()
  }));

  // Flag to track if data has been loaded
  private _dataLoaded = false;
  private _loadingInProgress = false;

  constructor(
    private mcDashboardService: McDashboardService,
    private notesService: NotesService,
    private recruitingService: RecruitingService,
    private financeService: FinanceService,
    private registrationService: RegistrationService,
    private customerService: CustomerService
  ) {
    this.customerService.customerServiceMessage$.subscribe(message => {
      this.refreshGlMail()
    })
  }

  /**
   * Initialize data loading if not already loaded
   */
  initializeData(): void {
    if (!this._dataLoaded && !this._loadingInProgress) {
      this.loadAllData();
    }
  }

  /**
   * Simplified data loading - load all data in parallel with proper error handling
   */
  private loadAllData(): void {
    this._loadingInProgress = true;
    this._isLoading.set(true);

    // Load all critical data in parallel
    const criticalData$ = combineLatest({
      bulletins: this.mcDashboardService.getActiveBulletins().pipe(
        startWith([]), // Start with empty array instead of null
        
        catchError(error => {
          return of([]);
        })
      ),
      knowBe4Data: this.mcDashboardService.getKnowBe4Data().pipe(
        startWith([]),
        
        catchError(error => {
          return of([]);
        })
      ),
      knowBe4Averages: this.mcDashboardService.getKnowBe4Averages().pipe(
        startWith({} as KnowBe4Averages),
        
        catchError(error => {
          return of({} as KnowBe4Averages);
        })
      ),
      glMail: this.notesService.getUnaddressedGlMail().pipe(
        startWith([]),
        
        catchError(error => {
          return of([]);
        })
      ),
      registrationProgress: this.registrationService.getRecruitingProgress().pipe(
        startWith(null),
        
        catchError(error => {
          return of(null);
        })
      ),
      callCounts: this.recruitingService.getCallCounts().pipe(
        startWith(null),
        
        catchError(error => {
          return of(null);
        })
      ),
      paymentProgress: this.financeService.getPaymentProgress().pipe(
        startWith(null),
        
        catchError(error => {
          return of(null);
        })
      ),
      callGoals: this.recruitingService.getCallGoals().pipe(
        startWith(null),
        
        catchError(error => {
          return of(null);
        })
      ),
      yearlyPayments: this.financeService.getPaymentStatsGraphData().pipe(
        startWith([]),
        
        catchError(error => {
          return of([]);
        })
      ),
      registrationGoals: this.registrationService.getRecruitingGoals().pipe(
        startWith(null),
        
        catchError(error => {
          return of(null);
        })
      ),
      callGraphData: this.recruitingService.getCallStatsGraphData().pipe(
        startWith(null),
        timeout(30000),
        catchError(error => {
          return of([]);
        })
      ),
      registrationGraphData: this.registrationService.getRegiStatsGraphData().pipe(
        startWith(null),
        
        catchError(error => {
          return of([]);
        })
      ),
      paymentGraphData: this.financeService.getPaymentStatsGraphData().pipe(
        startWith([]),
        
        catchError(error => {
          return of([]);
        })
      )
    });

    //NOTE: all the observables use startWith, so the tap will execute once for each observable, using the latest value emitted by each observable.
    //the finalize onle executes once they all complete.
    //not sure if signal change detection will be triggered simply by the set method being called, or if the object ref needs to change, too.
    criticalData$.pipe(
      tap(data => {
        // Update all signals with the loaded data
        this._bulletins.set(data.bulletins);
        this._knowBe4Data.set(data.knowBe4Data);
        this._knowBe4Averages.set(data.knowBe4Averages);
        this._glMail.set(data.glMail);
        this._callCounts.set(data.callCounts);
        this._callGoals.set(data.callGoals);
        this._paymentProgress.set(data.paymentProgress);
        this._yearlyPayments.set(data.yearlyPayments);
        this._registrationProgress.set(data.registrationProgress);
        this._registrationGoals.set(data.registrationGoals);
        this._callGraphData.set(data.callGraphData);
        this._registrationGraphData.set(data.registrationGraphData);
        this._paymentGraphData.set(data.paymentGraphData);
        this._lastUpdated.set(new Date());

        this.updateComponentLoadingStates();
      }),
      finalize(() => {
        this._loadingInProgress = false;
        this._isLoading.set(false);
        this._dataLoaded = true;

        // Update component loading states after data is loaded
    
      })
    ).subscribe();
  }

  refreshGlMail() {

    this._glMail.set([]);

    this._componentLoadingState.set({
      ...this._componentLoadingState(),
      glMail: true
    });

    this.notesService.getUnaddressedGlMail().pipe(
      timeout(30000),
      catchError(error => {
        return of([]);
      })
    ).subscribe(glMail => {
      this._glMail.set(glMail);
      this.updateComponentLoadingStates();
    })
  }

  /**
   * Update component loading states based on data availability
   */
  private updateComponentLoadingStates(): void {
    const currentState = this._componentLoadingState();

    // Update loading states based on data availability
    const newState: ComponentLoadingState = {
      ...currentState,
      pulse: !this._bulletins(),
      glMail: this._glMail() === null || this._glMail() === undefined, // Show component when data is loaded (even if empty array)
      knowBe4Graphs: !this._knowBe4Data() || this._knowBe4Data().length === 0,
      registrationStats: !this._registrationProgress(),
      callStats: !this._callCounts(),
      paymentStats: !this._paymentProgress()
    };

    this._componentLoadingState.set(newState);
  }

  /**
   * Refresh data - useful for manual refresh scenarios
   */
  refreshData(): void {
    this._dataLoaded = false;
    this.initializeData();
  }

  /**
   * Clear all data - useful for logout scenarios
   */
  clearData(): void {
    this._bulletins.set(null);
    this._knowBe4Data.set([]);
    this._knowBe4Averages.set({} as KnowBe4Averages);
    this._glMail.set([]);
    this._callCounts.set(null);
    this._callGoals.set(null);
    this._paymentProgress.set(null);
    this._yearlyPayments.set([]);
    this._registrationProgress.set(null);
    this._registrationGoals.set(null);
    this._callGraphData.set(null);
    this._registrationGraphData.set(null);
    this._paymentGraphData.set(null);
    this._isLoading.set(false);
    this._lastUpdated.set(null);
    this._dataLoaded = false;
    this._loadingInProgress = false;
  }
}